// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Box } from '@mui/material';
import { KpiCard } from '@hopdrive/sdk-ui';
import type { ProductCounts } from '@features/products/counts/productCounts';
import type { GuestRideStatusFilter } from '../stores/guestRideStore';

// TYPES ---------------------------------------------------------------- //

interface RidesTabStatsProps {
  counts: ProductCounts | undefined;
  selectedFilter: GuestRideStatusFilter;
  onFilterChange: (filter: GuestRideStatusFilter) => void;
  loading?: boolean;
}

interface StatCardDef {
  key: string;
  /** A tile without one is a figure alone. */
  filter?: GuestRideStatusFilter;
  label: string;
  subtitle: string;
  count: number;
}

// COMPONENT ---------------------------------------------------------------- //

/**
 * The list's completed filter lists completed and cancelled rides together, so the
 * Canceled tile is a figure with no list filter of its own.
 */
const RidesTabStats: React.FC<RidesTabStatsProps> = ({ counts, selectedFilter, onFilterChange, loading }) => {
  const buckets = counts?.buckets;
  const total = buckets?.total ?? 0;
  const deleted = counts?.deleted ?? 0;
  const pct = (n: number) => (total > 0 ? `${((n / total) * 100).toFixed(1)}% of total` : '0%');

  const cards: StatCardDef[] = [
    { key: 'all', filter: 'all', label: 'Total Rides', subtitle: 'All rides', count: total },
    { key: 'upcoming', filter: 'upcoming', label: 'Upcoming', subtitle: pct(buckets?.upcoming ?? 0), count: buckets?.upcoming ?? 0 },
    { key: 'active', filter: 'active', label: 'Active', subtitle: pct(buckets?.active ?? 0), count: buckets?.active ?? 0 },
    { key: 'completed', filter: 'completed', label: 'Completed', subtitle: pct(buckets?.completed ?? 0), count: buckets?.completed ?? 0 },
    { key: 'cancelled', label: 'Canceled', subtitle: pct(buckets?.cancelled ?? 0), count: buckets?.cancelled ?? 0 },
  ];

  if (deleted > 0 || selectedFilter === 'deleted') {
    cards.push({ key: 'deleted', filter: 'deleted', label: 'Deleted', subtitle: 'Deleted rides', count: deleted });
  }

  return (
    <Box sx={{ display: 'flex', gap: '20px', mb: 2.5 }}>
      {cards.map(card => (
        <KpiCard
          key={card.key}
          title={card.label}
          value={card.count}
          subtitle={card.subtitle}
          onClick={card.filter ? () => onFilterChange(card.filter!) : undefined}
          selected={card.filter !== undefined && selectedFilter === card.filter}
          loading={loading}
        />
      ))}
    </Box>
  );
};

// EXPORT ---------------------------------------------------------------- //

export default RidesTabStats;
